import React, { useEffect, useState } from 'react';
import { getOpenPositions } from '../api';

type LivePosition = {
  trade_id: string;
  instrument: string;
  direction: 'long' | 'short';
  quantity: number;
  entry_price: number;
  current_price: number;
  stop_price: number | null;
  unrealized_pnl: number;
  distance_to_stop_points: number | null;
  distance_to_stop_dollars: number | null;
  point_value: number;
  open_time: string | null;
  signal_class?: string | null;
  entry_mode?: string | null;
  quality_score?: number | null;
};

const formatElapsed = (seconds: number) => {
  if (seconds < 0) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const useTick = (ms: number = 1000) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
};

export default function LivePositionPanel() {
  const [positions, setPositions] = useState<LivePosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useTick(1000);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // /trading/open-positions returns { positions: [...], count: N }
        // (Fix 4: DB-authoritative read, replaces /positions/live which
        // read from the positions mirror and could show ghost rows).
        const r = await getOpenPositions();
        if (cancelled) return;
        setPositions(r.data?.positions || []);
        setError(null);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load live positions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="bg-gray-900 rounded-lg border border-gray-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wide">Live Positions</h2>
        <span className="text-xs text-gray-500">5s refresh</span>
      </div>
      {loading && positions.length === 0 ? (
        <p className="text-gray-600 text-sm">Loading...</p>
      ) : error ? (
        <p className="text-red-400 text-sm">{error}</p>
      ) : positions.length === 0 ? (
        <p className="text-gray-500 text-sm">No open positions</p>
      ) : (
        <div className="grid gap-2 md:grid-cols-2">
          {positions.map((p) => {
            const pnlPositive = p.unrealized_pnl >= 0;
            const elapsed = p.open_time
              ? Math.floor((Date.now() - new Date(p.open_time).getTime()) / 1000)
              : 0;
            return (
              <div
                key={p.trade_id || `${p.instrument}-${p.entry_price}`}
                className="bg-gray-800 rounded p-3 border border-gray-700"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                        p.direction === 'long'
                          ? 'bg-green-900 text-green-400'
                          : 'bg-red-900 text-red-400'
                      }`}
                    >
                      {p.direction.toUpperCase()}
                    </span>
                    <span className="font-medium text-white">{p.instrument}</span>
                    <span className="text-xs text-gray-500">
                      {p.quantity} contract{p.quantity === 1 ? '' : 's'}
                    </span>
                  </div>
                  <span
                    className={`font-mono text-base font-bold ${
                      pnlPositive ? 'text-green-400' : 'text-red-400'
                    }`}
                  >
                    {pnlPositive ? '+' : ''}${p.unrealized_pnl.toFixed(2)}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div>
                    <span className="text-gray-500">Entry</span>{' '}
                    <span className="font-mono text-gray-200">{p.entry_price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current</span>{' '}
                    <span className="font-mono text-gray-200">{p.current_price.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Stop</span>{' '}
                    <span className="font-mono text-gray-200">
                      {p.stop_price != null ? p.stop_price.toFixed(2) : '—'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">To Stop</span>{' '}
                    <span className="font-mono text-gray-200">
                      {p.distance_to_stop_points != null
                        ? `${p.distance_to_stop_points.toFixed(2)} pts`
                        : '—'}
                      {p.distance_to_stop_dollars != null && (
                        <span className="text-gray-500">
                          {' '}
                          (${p.distance_to_stop_dollars.toFixed(2)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="col-span-2 pt-1 border-t border-gray-700 mt-1">
                    <span className="text-gray-500">Time in trade</span>{' '}
                    <span className="font-mono text-gray-200">{formatElapsed(elapsed)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
